import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Typography, TextField, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, CircularProgress, Alert, Button,
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  Accordion, AccordionSummary, AccordionDetails, Collapse,
  FormControl, InputLabel, Select, MenuItem, FormHelperText,
} from "@mui/material";
import {
  Logout, ExpandMore, Add as AddIcon, History as HistoryIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { formatEST } from "../dateUtils";
import ThemeToggle from "./ThemeToggle";
// 2026-10-13 addition — same reset-credentials/change-email UI as
// AdminDashboard.js (shared component, not a duplicated copy) — a
// clinician row here is addressed by the same `sub` field
// admin_list_users now returns for every role.
import { ResetCredentialsButton, ChangeEmailButton } from "./AdminUserRecoveryActions";
import { extractErrorDetail } from "./validators";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const BRAND = { navy: "#00357a", gold: "#c9a96e" };

// 2026-10-23 addition (sales-rep lab-brand routing) — admin isn't tied
// to one brand the way a sales rep is, so this invite path requires an
// explicit pick, no default. Hand-duplicated in every place this app
// needs the same two options/labels (same convention as
// AdminSalesReps.js's own identical constant and _normalize_phone's own
// hand-duplicated-helper precedent) — keep exactly in sync with
// main.py's own LAB_BRAND_DISPLAY_NAMES.
const LAB_BRAND_OPTIONS = [
  { value: "infinity", label: "Infinity Labs" },
  { value: "apex", label: "APEX Labs" },
];

// Same cross-app audit click-through AdminDashboard.js already uses — the
// Audit Dashboard itself lives in labariumaireport's frontend
// (AuditDashboard.js, route /admin/audit-log), which already supports
// filtering by identity via the `identity` query param. Reused here
// unchanged (same helper, copied rather than imported since these are two
// separate React apps/bundles, same note as AdminDashboard.js's own copy).
function reportAuditLogUrl(identity) {
  const base = process.env.REACT_APP_REPORT_ADMIN_URL;
  if (!base) return null;
  let origin;
  try {
    origin = new URL(base).origin;
  } catch {
    return null;
  }
  return `${origin}/admin/audit-log?source=portal&identity=${encodeURIComponent(identity)}`;
}

// 2026-10-23 fix (clinician invite-only build, Piece 3) — public clinician
// self-registration is closed, and this admin-create-with-upfront-practice/
// NPI dialog is replaced by the same email-only invite everywhere (decision
// 8: one consistent path, not two). The invited clinician fills in their
// own name/practice/NPI/signature on first login (the identity-completion
// gate, a later piece) — this dialog no longer collects any of that
// upfront, so the NPPES confirm-dialog step (useNpiConfirm) moves there too.
function NewClinicianDialog({ open, onClose, onCreated, token }) {
  const [email, setEmail] = useState("");
  const [labBrand, setLabBrand] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleClose = () => {
    if (submitting) return;
    setEmail("");
    setLabBrand("");
    setError(null);
    setSuccess(null);
    onClose();
  };

  const handleSubmit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_BASE}/admin/clinicians/invite`,
        { email: email.trim(), lab_brand: labBrand },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // 2026-10-23 fix (security-auditor finding) — this used to show the
      // same "Invite sent" success alert regardless of whether the email
      // actually went out, silently telling the admin it worked even on
      // email_sent: false (and never surfacing the returned temp_password,
      // the only way to hand it to the clinician another way).
      setSuccess({
        email: res.data?.email || email.trim(),
        emailSent: res.data?.email_sent !== false,
        tempPassword: res.data?.temp_password || null,
      });
      onCreated();
    } catch (err) {
      setError(extractErrorDetail(err, "Could not send this invite."));
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim()) && !!labBrand;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, color: BRAND.navy }}>Invite Clinician</DialogTitle>
      <DialogContent>
        {success ? (
          success.emailSent ? (
            <Alert severity="success" sx={{ mt: 1 }}>
              Invite sent to <strong>{success.email}</strong>. They'll set their own password and complete
              their profile (name, practice, NPI, signature) on first login.
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Account created for <strong>{success.email}</strong>, but the invite email could not be
              sent. Hand this temporary password to the clinician directly:
              <Box component="code" sx={{ display: "block", mt: 1, fontWeight: 700 }}>{success.tempPassword}</Box>
            </Alert>
          )
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField label="Email" type="email" value={email} fullWidth autoFocus
              helperText="They'll receive a temporary password and finish setting up their own account."
              onChange={(e) => setEmail(e.target.value)} />
            <FormControl fullWidth>
              <InputLabel id="clinician-invite-lab-brand-label">Lab Brand</InputLabel>
              <Select
                labelId="clinician-invite-lab-brand-label" label="Lab Brand" value={labBrand}
                onChange={(e) => setLabBrand(e.target.value)}
              >
                {LAB_BRAND_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Determines which lab this clinician's orders and reports are routed to — can't be changed later.
              </FormHelperText>
            </FormControl>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {success ? (
          <Button onClick={handleClose} variant="contained" sx={{ bgcolor: BRAND.navy }}>Done</Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained" disabled={!canSubmit || submitting}
              sx={{ bgcolor: BRAND.navy }}>
              {submitting ? "Sending…" : "Send Invite"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

function ClinicianDetailRow({ clinician, token, onAccountChanged }) {
  const [expanded, setExpanded] = useState(false);
  const [registryInfo, setRegistryInfo] = useState(null);
  const [registryLoading, setRegistryLoading] = useState(false);

  const toggle = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !registryInfo && clinician.npi) {
      setRegistryLoading(true);
      try {
        const res = await axios.get(`${API_BASE}/admin/provider-registry/${clinician.npi}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setRegistryInfo(res.data);
      } catch {
        setRegistryInfo({ linked_accounts: [] });
      } finally {
        setRegistryLoading(false);
      }
    }
  };

  const otherPractices = (registryInfo?.linked_accounts || [])
    .map((a) => a.practice_name)
    .filter((p) => p && p !== clinician.practice_name);

  const auditUrl = clinician.email ? reportAuditLogUrl(clinician.email) : null;

  return (
    <>
      <TableRow hover sx={{ cursor: "pointer" }} onClick={toggle}>
        <TableCell>{(clinician.first_name || clinician.last_name) ? `${clinician.first_name || ""} ${clinician.last_name || ""}`.trim() : "—"}</TableCell>
        <TableCell>{clinician.npi || "—"}</TableCell>
        <TableCell>{clinician.email}</TableCell>
        <TableCell>{clinician.lab_brand_display || "—"}</TableCell>
        <TableCell>{formatEST(clinician.created_at, { dateOnly: true })}</TableCell>
        <TableCell>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
            <Chip size="small"
              label={clinician.force_password_change ? "Pending first login" : "Active"}
              color={clinician.force_password_change ? "warning" : "success"} />
            {/* 2026-10-18 fix (admin field-visibility sweep) — NPI
                verification outcome was recorded at creation time but never
                surfaced on this admin page. */}
            {clinician.npi_verification?.outcome && (
              <Tooltip title={`NPI verification: ${clinician.npi_verification.outcome}${clinician.npi_verification.user_confirmed ? " (confirmed by user)" : ""}`}>
                <Chip size="small" variant="outlined"
                  label={`NPI: ${clinician.npi_verification.outcome}`}
                  color={clinician.npi_verification.outcome === "matched" ? "success" : "warning"} />
              </Tooltip>
            )}
          </Box>
        </TableCell>
        <TableCell>
          {auditUrl && (
            <Tooltip title="View audit log">
              <IconButton size="small" href={auditUrl} target="_blank" rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}>
                <HistoryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
        <TableCell onClick={(e) => e.stopPropagation()}>
          {/* 2026-10-13 addition — reset-credentials/change-email had ZERO
              frontend caller anywhere in this app. Disabled (not hidden)
              when clinician.sub is missing — same reasoning as
              AdminDashboard.js's own copy of this guard. */}
          <Box sx={{ display: "flex", gap: 0.25 }}>
            <ResetCredentialsButton sub={clinician.sub} email={clinician.email} token={token} iconOnly onDone={onAccountChanged} />
            <ChangeEmailButton sub={clinician.sub} email={clinician.email} token={token} iconOnly
              onChanged={onAccountChanged} />
          </Box>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={8} sx={{ py: 0, border: expanded ? undefined : "none" }}>
          <Collapse in={expanded} unmountOnExit>
            <Box sx={{ py: 2, px: 1 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#64748b", mb: 1 }}>
                FULL ACCOUNT INFO
              </Typography>
              <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap", mb: 1.5 }}>
                <Box><Typography sx={{ fontSize: 12, color: "#94a3b8" }}>Practice</Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{clinician.practice_name || "—"}</Typography></Box>
                <Box><Typography sx={{ fontSize: 12, color: "#94a3b8" }}>NPI</Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{clinician.npi || "—"}</Typography></Box>
                <Box><Typography sx={{ fontSize: 12, color: "#94a3b8" }}>Email</Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{clinician.email}</Typography></Box>
                <Box><Typography sx={{ fontSize: 12, color: "#94a3b8" }}>Registered</Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{formatEST(clinician.created_at)}</Typography></Box>
              </Box>

              {registryLoading && <CircularProgress size={16} />}
              {!registryLoading && clinician.npi && (
                otherPractices.length > 0 ? (
                  <Alert severity="info" sx={{ maxWidth: 560 }}>
                    This NPI is also registered at: {otherPractices.join(", ")}
                  </Alert>
                ) : (
                  <Typography sx={{ fontSize: 12.5, color: "#94a3b8" }}>
                    No other practices share this NPI.
                  </Typography>
                )
              )}

              {auditUrl && (
                <Box sx={{ mt: 1.5 }}>
                  <Button size="small" startIcon={<HistoryIcon fontSize="small" />}
                    href={auditUrl} target="_blank" rel="noopener noreferrer"
                    sx={{ textTransform: "none" }}>
                    View full audit trail for this clinician
                  </Button>
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function AdminClinicians() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();

  const [clinicians, setClinicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const admin = JSON.parse(localStorage.getItem("admin") || "null");

  useEffect(() => {
    const fetchClinicians = async () => {
      try {
        const res = await axios.get(`${API_BASE}/admin/users`, {
          headers: { Authorization: `Bearer ${admin?.token}` },
          params: { page_size: 500 },
        });
        const all = res.data.users || [];
        setClinicians(all.filter((u) => u.role === "clinician"));
      } catch (err) {
        if (err.response?.status === 401 || err.response?.status === 403) {
          localStorage.removeItem("admin");
          navigate("/admin-login", { replace: true });
          return;
        }
        setError(extractErrorDetail(err, "Failed to load clinicians."));
      } finally {
        setLoading(false);
      }
    };
    fetchClinicians();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const grouped = useMemo(() => {
    const byPractice = {};
    for (const c of clinicians) {
      const key = c.practice_name || "(No practice on file)";
      if (!byPractice[key]) byPractice[key] = [];
      byPractice[key].push(c);
    }
    return Object.entries(byPractice).sort((a, b) => a[0].localeCompare(b[0]));
  }, [clinicians]);

  const handleLogout = () => {
    localStorage.removeItem("admin");
    navigate("/admin-login", { replace: true });
  };

  const bg = isDark ? "#080d1a" : "#f4f7ff";

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: bg }}>
      {/* Same navy+gold admin navbar as AdminDashboard.js — this app's one
          established admin-page chrome convention. */}
      <Box sx={{ bgcolor: "rgba(0,14,36,0.96)", backdropFilter: "blur(20px)", borderBottom: "1px solid rgba(201,169,110,0.2)" }}>
        <Box sx={{ height: 2, background: "linear-gradient(90deg, transparent, rgba(201,169,110,0.8) 30%, rgba(223,192,138,0.9) 50%, rgba(201,169,110,0.8) 70%, transparent)" }} />
        <Box sx={{ maxWidth: 1200, mx: "auto", px: 3, py: 1, minHeight: 60,
          display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", rowGap: 1 }}>
          <img src="/logo-labarium-dark.png" alt="Labarium" style={{ height: 38, width: "auto" }} />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ThemeToggle />
            <Tooltip title="Log out">
              <IconButton size="small" onClick={handleLogout}
                sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.08)" } }}>
                <Logout fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      <Box sx={{ px: { xs: 2, md: 5 }, py: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", rowGap: 1 }}>
          <Box>
            <Button size="small" startIcon={<ArrowBackIcon fontSize="small" />}
              onClick={() => navigate("/admin-dashboard")}
              sx={{ textTransform: "none", fontWeight: 600, color: "#888", mb: 0.5 }}>
              User Dashboard
            </Button>
            <Typography sx={{ fontSize: 13.5, fontWeight: 700, letterSpacing: "0.12em", color: BRAND.gold, mb: 0.5 }}>
              ADMIN
            </Typography>
            <Typography sx={{ fontSize: 26, fontWeight: 800, color: isDark ? "#f1f5f9" : BRAND.navy }}>
              Clinicians
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}
            sx={{ bgcolor: BRAND.navy, textTransform: "none", fontWeight: 700 }}>
            New Clinician
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {grouped.length === 0 && !error && (
          <Alert severity="info">No clinicians yet. Use "New Clinician" to create the first account.</Alert>
        )}

        {grouped.map(([practiceName, rows]) => (
          <Accordion key={practiceName} defaultExpanded={grouped.length === 1}
            sx={{ mb: 1.5, borderRadius: 2, "&:before": { display: "none" } }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Typography sx={{ fontWeight: 700, color: isDark ? "#f1f5f9" : BRAND.navy }}>
                  {practiceName}
                </Typography>
                <Chip size="small" label={`${rows.length} clinician${rows.length === 1 ? "" : "s"}`} />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <TableContainer component={Paper} elevation={0}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>NPI</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Lab Brand</TableCell>
                      <TableCell>Registered</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Audit</TableCell>
                      <TableCell>Account</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((c) => (
                      <ClinicianDetailRow key={c.sub || c.email} clinician={c} token={admin?.token}
                        onAccountChanged={() => setRefreshKey((k) => k + 1)} />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      <NewClinicianDialog
        open={dialogOpen}
        token={admin?.token}
        onClose={() => setDialogOpen(false)}
        onCreated={() => setRefreshKey((k) => k + 1)}
      />
    </Box>
  );
}

export default AdminClinicians;
